import React from 'react';
import './about.css';
import { Navbar, Header, Footer } from '../../components';
import { Container, Row, Col, Image } from 'react-bootstrap';
import myimage from '../../assets/TMF PERSONAL POTRAIT.jpg';

const About = () => {
  return (
    <div classname='about-page'>
      <Navbar />
      <Header />

      <Container className="mt-5">

        {/* =========================
            HERO / INTRODUCTION
        ========================= */}
        <Row className="align-items-center mb-5">

          <Col md={6} className="text-center">
            <Image
              src={myimage}
              roundedCircle
              fluid
              alt="Goodness Ononogbu"
              className="about-image shadow-lg"
            />
          </Col>

          <Col md={6}>

            <h1 className="display-4 fw-bold">
              Hi, I’m Goodness Ononogbu.
            </h1>

            <h2 className="h4 text-muted mb-3">
              I work at the intersection of{' '}
              <strong>Infrastructure + Impact.</strong>
            </h2>

            <p className="lead">
              I am a builder and thinker interested in how intelligent
              technology can help people understand, interact with and
              improve the physical world.
            </p>

            <p>
              My work brings together Artificial Intelligence, spatial
              computing, digital twins, robotics and sustainability —
              not simply as technologies to experiment with, but as tools
              for designing better systems.
            </p>

          </Col>
        </Row>


        {/* =========================
            THE THESIS
        ========================= */}
        <Row className="py-5">

          <Col lg={10}>

            <p className="text-uppercase text-muted fw-bold small">
              THE QUESTION I KEEP RETURNING TO
            </p>

            <h2 className="display-6 fw-bold">
              What happens when technology stops being
              something we simply look at?
            </h2>

            <p className="lead mt-4">
              Much of modern technology helps us understand the world.
              AI analyses it. Dashboards display it. Digital twins model it.
              Sensors measure it.
            </p>

            <p>
              I am interested in the layer that comes next:
              <strong> what helps people act within that world?</strong>
            </p>

            <p>
              How do we make complex environments understandable?
              How do we coordinate participation?
              How do we verify what actually happened?
              And how do we connect individual actions to measurable
              outcomes?
            </p>

          </Col>

        </Row>


        {/* =========================
            KERUVVA
        ========================= */}
        <Row className="py-5">

          <Col>

            <div className="p-4 p-md-5 bg-light rounded">

              <p className="text-uppercase text-primary fw-bold small">
                WHAT I AM BUILDING
              </p>

              <h2 className="display-6 fw-bold mb-4">
                Keruvva
              </h2>

              <p className="lead">
                A participation infrastructure platform for the physical
                world.
              </p>

              <p>
                Keruvva explores how digital representations of physical
                environments can become a new layer for participation.
              </p>

              <p>
                Institutions can define real-world projects and initiatives.
                Those projects can be translated into structured actions
                that people can discover, understand, participate in and
                complete.
              </p>

              <p>
                AI and digital twins provide the intelligence and spatial
                context. Human participation provides the execution.
                Verification connects action to evidence.
              </p>

              <p className="fw-bold">
                The ambition is to build infrastructure that connects
                people, projects and proof — so real-world action becomes
                more structured, measurable and scalable.
              </p>

              <a
                href="keruvva.com"
                className="btn btn-dark mt-3"
              >
                EXPLORE KERUVVA ↗
              </a>

            </div>

          </Col>

        </Row>


        {/* =========================
            AREAS OF WORK
        ========================= */}
        <Row className="py-5">

          <Col>

            <h2 className="fw-bold">
              Areas I Work Across
            </h2>

            <p className="text-muted mb-4">
              I deliberately work across disciplines because the problems
              I am interested in rarely belong to one field.
            </p>

          </Col>

        </Row>

        <Row className="g-4">

          <Col md={6} lg={3}>
            <div className="p-4 h-100 border rounded">
              <h4 className="fw-bold">Artificial Intelligence</h4>
              <p className="text-muted mb-0">
                Intelligent systems, machine learning, AI-enabled
                decision-making and human-in-the-loop systems.
              </p>
            </div>
          </Col>

          <Col md={6} lg={3}>
            <div className="p-4 h-100 border rounded">
              <h4 className="fw-bold">Spatial Computing</h4>
              <p className="text-muted mb-0">
                Digital twins, XR and interfaces that connect digital
                information with physical environments.
              </p>
            </div>
          </Col>

          <Col md={6} lg={3}>
            <div className="p-4 h-100 border rounded">
              <h4 className="fw-bold">Infrastructure</h4>
              <p className="text-muted mb-0">
                Systems designed to coordinate people, information,
                resources and action at scale.
              </p>
            </div>
          </Col>

          <Col md={6} lg={3}>
            <div className="p-4 h-100 border rounded">
              <h4 className="fw-bold">Sustainability</h4>
              <p className="text-muted mb-0">
                Applying technology to environmental, urban and social
                challenges where measurable impact matters.
              </p>
            </div>
          </Col>

        </Row>


        {/* =========================
            HOW I THINK
        ========================= */}
        <Row className="py-5 mt-4">

          <Col lg={10}>

            <p className="text-uppercase text-muted fw-bold small">
              HOW I THINK
            </p>

            <h2 className="display-6 fw-bold">
              I am less interested in technology for its own sake.
            </h2>

            <p className="lead mt-4">
              I care about what technology makes possible.
            </p>

            <p>
              I like starting with the system rather than the feature:
              understanding the problem, the incentives, the people,
              the infrastructure and the constraints before deciding
              what should be built.
            </p>

            <p>
              That means moving between disciplines — technical research,
              product design, sustainability, spatial computing and
              systems thinking — and looking for the connections between
              them.
            </p>

            <p>
              I am still learning, still experimenting and still building.
              But the direction is increasingly clear:
              <strong> intelligent infrastructure should help people
              participate in shaping the world around them.</strong>
            </p>

          </Col>

        </Row>


        {/* =========================
            BACKGROUND / CAPABILITIES
        ========================= */}
        <Row className="py-5 bg-light">

          <Col md={6}>

            <h2 className="fw-bold">
              Technical Foundation
            </h2>

            <ul className="mt-4">
              <li className="mb-2">
                Artificial Intelligence & Machine Learning
              </li>
              <li className="mb-2">
                Web Development & Interactive Applications
              </li>
              <li className="mb-2">
                Spatial Computing & XR
              </li>
              <li className="mb-2">
                Digital Twins & 3D Environments
              </li>
              <li className="mb-2">
                Data Analysis & Intelligent Systems
              </li>
              <li className="mb-2">
                UI/UX & Product Prototyping
              </li>
            </ul>

          </Col>

          <Col md={6}>

            <h2 className="fw-bold">
              What I Value
            </h2>

            <ul className="mt-4">
              <li className="mb-2">
                First-principles thinking
              </li>
              <li className="mb-2">
                Long-term systems thinking
              </li>
              <li className="mb-2">
                Human-centred technology
              </li>
              <li className="mb-2">
                Measurable real-world outcomes
              </li>
              <li className="mb-2">
                Continuous learning
              </li>
              <li className="mb-2">
                Building before over-explaining
              </li>
            </ul>

          </Col>

        </Row>


        {/* =========================
            CONNECT
        ========================= */}
        <Row className="connect-section text-center py-5">

          <Col>

            <h2 className="fw-bold">
              Let’s Build What Comes Next.
            </h2>

            <p className="lead mb-4">
              I am interested in ambitious ideas, difficult problems
              and people building at the edge of what is possible.
            </p>

            <a
              href="https://www.linkedin.com/in/goodness-ononogbu/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none me-3"
              aria-label="Goodness Ononogbu on LinkedIn"
            >
              <i className="bi bi-linkedin fs-3"></i>
            </a>

            <a
              href="mailto:ononogbugoodness@gmail.com"
              className="text-decoration-none me-3"
              aria-label="Email Goodness Ononogbu"
            >
              <i className="bi bi-envelope fs-3"></i>
            </a>

          </Col>

        </Row>

      </Container>

      <Footer />
    </div>
  );
};

export default About;